import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { FocusTrap } from 'focus-trap-react';

interface AskContextualizeQuestionsStepProps {
  onboardingStatus: any;
  organizationId?: string;
  governanceOverlays?: Array<{ id: string; name: string; description?: string; descriptions?: Array<{ key: string; value: any; is_active: boolean }> }>;
  apArchetypes?: APArchetype[];
  customers?: Customer[];
  customersInSections?: CustomerInSection[];
  setCustomersInSections?: React.Dispatch<React.SetStateAction<CustomerInSection[]>>;
  customBehaviors?: CustomBehavior[];
  onUpdateCustomer?: (updatedCustomer: Customer) => void;
  onAddCustomBehavior?: (behavior: CustomBehavior) => void;
  onAddCustomer?: (newCustomer: Customer) => void;
  onRemoveCustomer?: (customerId: string) => void;
  onNextStep?: () => void;
  publicMode?: boolean;
}

interface APArchetype {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  descriptions: Array<{
    id: string;
    key: string;
    value: any;
    is_active: boolean;
  }>;
}

interface Customer {
  id: string;
  name: string;
  xero_direct_contact_id?: string;
  ap_archetype?: {
    id: string;
    name: string;
    description: string;
  };
  governance_overlay?: {
    id: string;
    name: string;
    description: string;
  };
  custom_behavior?: {
    id: string;
    name: string;
    description: string;
  };
  rubrics?: {
    credit_risk_profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning';
  };
  // Multiple attributes for customers in multiple sections
  multiple_attributes?: {
    ap_archetypes?: Array<{
      id: string;
      name: string;
      description: string;
    }>;
    governance_overlays?: Array<{
      id: string;
      name: string;
      description: string;
    }>;
    custom_behaviors?: Array<{
      id: string;
      name: string;
      description: string;
    }>;
  };
}

interface CustomerInSection {
  id: string;
  customerId: string;
  name: string;
  sectionType: 'ap_archetype' | 'governance_overlay' | 'custom_behavior';
  sectionId: string;
  isDuplicated: boolean;
}

interface CustomBehavior {
  id: string;
  name: string;
  description: string;
}

const AskContextualizeQuestionsStep: React.FC<AskContextualizeQuestionsStepProps> = ({
  onboardingStatus,
  organizationId,
  governanceOverlays = [],
  apArchetypes = [],
  customers = [],
  customersInSections = [],
  setCustomersInSections,
  customBehaviors = [],
  onUpdateCustomer,
  onAddCustomBehavior,
  onAddCustomer,
  onRemoveCustomer,
  onNextStep
}) => {
  const { accessToken, setUserAndToken } = useUser();
  const [draggedCustomer, setDraggedCustomer] = useState<CustomerInSection | null>(null);
  const [showBehaviorModal, setShowBehaviorModal] = useState(false);
  const [newBehaviorDescription, setNewBehaviorDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(40); // Show 40 customers per page
  const [noDunningCurrentPage, setNoDunningCurrentPage] = useState(1);
  const [noDunningItemsPerPage] = useState(30); // Show 30 customers per page for No Dunning
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // customersInSections is now managed by parent component

  // Focus management and escape key handling for modal
  useEffect(() => {
    if (showBehaviorModal) {
      // Set focus to textarea when modal opens
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
      
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      
      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleCancelBehavior();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      // Cleanup
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showBehaviorModal]);

  const getStepStatus = () => {
    if (!onboardingStatus?.xero_authorization?.is_authorized) {
      return 'waiting';
    }
    
    return 'active';
  };

  const status = getStepStatus();

  // Initialize customersInSections when component loads
  useEffect(() => {
    if (customers.length === 0 || apArchetypes.length === 0) return;
    
    // Check if customersInSections is already initialized
    if (customersInSections.length > 0) return;
    
    // Separate customers with "No Dunning" credit risk profile
    const noDunningCustomers = customers.filter(customer => 
      customer.rubrics?.credit_risk_profile === 'No Dunning'
    );
    const filteredCustomers = customers.filter(customer => 
      customer.rubrics?.credit_risk_profile !== 'No Dunning'
    );
    
    // Create initial customer_in_section objects for filtered customers
    const initialCustomersInSections: CustomerInSection[] = [];
    
    // Add "No Dunning" customers to a special section first
    noDunningCustomers.forEach((customer) => {
      const noDunningSection: CustomerInSection = {
        id: `customer_${customer.id}_no_dunning`,
        customerId: customer.id,
        name: customer.name,
        sectionType: 'custom_behavior', // Use custom_behavior type for No Dunning
        sectionId: 'no_dunning_section',
        isDuplicated: false
      };
      initialCustomersInSections.push(noDunningSection);
    });
    
    filteredCustomers.forEach((customer, index) => {
      let targetArchetype: string;
      
      if (index < 10) {
        targetArchetype = 'STANDARD_B2B';
      } else if (index < 12) {
        targetArchetype = 'CORP_PORTAL';
      } else if (index < 14) {
        targetArchetype = 'OWNER_OPERATOR';
      } else {
        // For customers beyond the first 14, cycle back to STANDARD_B2B
        targetArchetype = 'STANDARD_B2B';
      }
      
      const archetype = apArchetypes.find(a => a.name === targetArchetype);
      if (archetype) {
        const customerInSection: CustomerInSection = {
          id: `customer_${customer.id}_${archetype.id}`,
          customerId: customer.id,
          name: customer.name,
          sectionType: 'ap_archetype',
          sectionId: archetype.id,
          isDuplicated: false
        };
        initialCustomersInSections.push(customerInSection);
        
        // Update the customer with the archetype
        if (onUpdateCustomer) {
          const updatedCustomer = {
            ...customer,
            ap_archetype: { 
              id: archetype.id, 
              name: archetype.name, 
              description: archetype.description 
            }
          };
          onUpdateCustomer(updatedCustomer);
        }
      }
    });
    
    if (setCustomersInSections) {
      setCustomersInSections(initialCustomersInSections);
    }
  }, [customers, apArchetypes.length, setCustomersInSections]); // Only run when these change

  // Search and pagination logic for STANDARD_B2B customers
  const getFilteredAndPaginatedStandardB2BCustomers = () => {
    const standardB2BCustomers = getCustomersByArchetype()['STANDARD_B2B'] || [];
    
    // Filter customers based on search term
    const filteredCustomers = standardB2BCustomers.filter(customerInSection =>
      customerInSection.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Calculate pagination
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);
    
    return {
      customers: paginatedCustomers,
      totalPages,
      currentPage,
      totalCustomers: filteredCustomers.length,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1
    };
  };

  // Pagination logic for No Dunning customers
  const getFilteredAndPaginatedNoDunningCustomers = () => {
    const noDunningCustomers = getCustomersByArchetype()['NO_DUNNING'] || [];
    
    // Calculate pagination
    const totalPages = Math.ceil(noDunningCustomers.length / noDunningItemsPerPage);
    const startIndex = (noDunningCurrentPage - 1) * noDunningItemsPerPage;
    const endIndex = startIndex + noDunningItemsPerPage;
    const paginatedCustomers = noDunningCustomers.slice(startIndex, endIndex);
    
    return {
      customers: paginatedCustomers,
      totalPages,
      currentPage: noDunningCurrentPage,
      totalCustomers: noDunningCustomers.length,
      hasNextPage: noDunningCurrentPage < totalPages,
      hasPrevPage: noDunningCurrentPage > 1
    };
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination control functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    const { totalPages } = getFilteredAndPaginatedStandardB2BCustomers();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // No Dunning pagination control functions
  const handleNoDunningPageChange = (page: number) => {
    setNoDunningCurrentPage(page);
  };

  const handleNoDunningNextPage = () => {
    const { totalPages } = getFilteredAndPaginatedNoDunningCustomers();
    if (noDunningCurrentPage < totalPages) {
      setNoDunningCurrentPage(noDunningCurrentPage + 1);
    }
  };

  const handleNoDunningPrevPage = () => {
    if (noDunningCurrentPage > 1) {
      setNoDunningCurrentPage(noDunningCurrentPage - 1);
    }
  };

  // Group customers by their current AP archetype (based on customersInSections)
  const getCustomersByArchetype = () => {
    const grouped: { [key: string]: CustomerInSection[] } = {};
    
    // Initialize all archetypes
    apArchetypes.forEach(archetype => {
      grouped[archetype.name] = [];
    });
    
    // Initialize No Dunning section
    grouped['NO_DUNNING'] = [];
    
    // Process customersInSections for AP archetypes
    customersInSections.forEach(customerInSection => {
      if (customerInSection.sectionType === 'ap_archetype') {
        const archetype = apArchetypes.find(a => a.id === customerInSection.sectionId);
        if (archetype) {
          if (grouped[archetype.name]) {
            grouped[archetype.name].push(customerInSection);
          } else {
            // If archetype doesn't exist in the list, put in STANDARD_B2B
            if (!grouped['STANDARD_B2B']) {
              grouped['STANDARD_B2B'] = [];
            }
            grouped['STANDARD_B2B'].push(customerInSection);
          }
        }
      } else if (customerInSection.sectionType === 'custom_behavior' && customerInSection.sectionId === 'no_dunning_section') {
        grouped['NO_DUNNING'].push(customerInSection);
      }
    });
    

    // Find customers without archetypes (customers not in customersInSections)
    const customersWithArchetypes = new Set(customersInSections.map(cis => cis.customerId));
    const customersWithoutArchetype = customers.filter(customer => 
      !customersWithArchetypes.has(customer.id) && 
      customer.rubrics?.credit_risk_profile !== 'No Dunning'
    );
    
    // Distribute customers without archetypes
    customersWithoutArchetype.forEach((customer: any, index: number) => {
      let targetArchetype: string;
      
      if (index < 10) {
        targetArchetype = 'STANDARD_B2B';
      } else if (index < 12) {
        targetArchetype = 'CORP_PORTAL';
      } else if (index < 14) {
        targetArchetype = 'OWNER_OPERATOR';
      } else {
        // For customers beyond the first 14, cycle back to STANDARD_B2B
        targetArchetype = 'STANDARD_B2B';
      }
      
      if (!grouped[targetArchetype]) {
        grouped[targetArchetype] = [];
      }
      
      // Create a CustomerInSection object for the customer without archetype
      const archetype = apArchetypes.find(a => a.name === targetArchetype);
      if (archetype) {
        const customerInSection: CustomerInSection = {
          id: `customer_${customer.id}_${archetype.id}`,
          customerId: customer.id,
          name: customer.name,
          sectionType: 'ap_archetype',
          sectionId: archetype.id,
          isDuplicated: false
        };
        grouped[targetArchetype].push(customerInSection);
      }
    });
    
    return grouped;
  };

  const handleDragStart = (e: React.DragEvent, customerInSection: CustomerInSection) => {
    setDraggedCustomer(customerInSection);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetName: string) => {
    e.preventDefault();
    
    if (!draggedCustomer) return;
    
    // Find the actual customer object
    const customer = customers.find(c => c.id === draggedCustomer.customerId);
    if (!customer) return;
    
    // Handle different drop targets
    if (targetName === 'STANDARD_B2B') {
      // Find the STANDARD_B2B archetype
      const targetArchetype = apArchetypes.find(arch => arch.name === 'STANDARD_B2B');
      if (!targetArchetype) return;
      
      // Remove the specific dragged customer_in_section
      if (setCustomersInSections) {
        setCustomersInSections(prev => 
          prev.filter(cis => cis.id !== draggedCustomer.id)
        );
      }
      
      // Add new customer_in_section
      const newCustomerInSection: CustomerInSection = {
        id: `customer_${draggedCustomer.customerId}_${targetArchetype.id}`,
        customerId: draggedCustomer.customerId,
        name: draggedCustomer.name,
        sectionType: 'ap_archetype',
        sectionId: targetArchetype.id,
        isDuplicated: draggedCustomer.isDuplicated
      };
      
      if (setCustomersInSections) {
        setCustomersInSections(prev => [...prev, newCustomerInSection]);
      }
      
      // Update the customer object
      if (draggedCustomer.isDuplicated) {
        // For duplicated customers, just add the new attribute
        updateCustomerAttributes(customer, { id: targetArchetype.id, name: targetArchetype.name, description: targetArchetype.description }, 'ap_archetype');
      } else {
        // For original customers, replace all attributes
        if (onUpdateCustomer) {
          const updatedCustomer = {
            ...customer,
            ap_archetype: { id: targetArchetype.id, name: targetArchetype.name, description: targetArchetype.description },
            governance_overlay: undefined,
            custom_behavior: undefined
          };
          onUpdateCustomer(updatedCustomer);
        }
      }
    } else if (targetName.startsWith('GOVERNANCE_OVERLAY_')) {
      // Handle governance overlay assignments
      const overlayId = targetName.replace('GOVERNANCE_OVERLAY_', '');
      const targetOverlay = governanceOverlays.find(overlay => overlay.id === overlayId);
      if (!targetOverlay) return;
      
      // Remove the specific dragged customer_in_section
      if (setCustomersInSections) {
        setCustomersInSections(prev => 
          prev.filter(cis => cis.id !== draggedCustomer.id)
        );
      }
      
      // Add new customer_in_section
      const newCustomerInSection: CustomerInSection = {
        id: `customer_${draggedCustomer.customerId}_${overlayId}`,
        customerId: draggedCustomer.customerId,
        name: draggedCustomer.name,
        sectionType: 'governance_overlay',
        sectionId: overlayId,
        isDuplicated: draggedCustomer.isDuplicated
      };
      
      if (setCustomersInSections) {
        setCustomersInSections(prev => [...prev, newCustomerInSection]);
      }
      
      // Update the customer object
      if (draggedCustomer.isDuplicated) {
        // For duplicated customers, just add the new attribute
        updateCustomerAttributes(customer, { 
          id: targetOverlay.id, 
          name: targetOverlay.name, 
          description: targetOverlay.description || '' 
        }, 'governance_overlay');
      } else {
        // For original customers, replace all attributes
        if (onUpdateCustomer) {
          const updatedCustomer = {
            ...customer,
            governance_overlay: { 
              id: targetOverlay.id, 
              name: targetOverlay.name, 
              description: targetOverlay.description || '' 
            },
            ap_archetype: undefined,
            custom_behavior: undefined
          };
          onUpdateCustomer(updatedCustomer);
        }
      }
    } else if (targetName.startsWith('CUSTOM_BEHAVIOR_')) {
      // Handle custom behavior assignments
      const behaviorId = targetName.replace('CUSTOM_BEHAVIOR_', '');
      const targetBehavior = customBehaviors.find(behavior => behavior.id === behaviorId);
      if (!targetBehavior) return;
      
      // Remove the specific dragged customer_in_section
      if (setCustomersInSections) {
        setCustomersInSections(prev => 
          prev.filter(cis => cis.id !== draggedCustomer.id)
        );
      }
      
      // Add new customer_in_section
      const newCustomerInSection: CustomerInSection = {
        id: `customer_${draggedCustomer.customerId}_${behaviorId}`,
        customerId: draggedCustomer.customerId,
        name: draggedCustomer.name,
        sectionType: 'custom_behavior',
        sectionId: behaviorId,
        isDuplicated: draggedCustomer.isDuplicated
      };
      
      if (setCustomersInSections) {
        setCustomersInSections(prev => [...prev, newCustomerInSection]);
      }
      
      // Update the customer object
      if (draggedCustomer.isDuplicated) {
        // For duplicated customers, just add the new attribute
        updateCustomerAttributes(customer, { 
          id: targetBehavior.id, 
          name: targetBehavior.name, 
          description: targetBehavior.description 
        }, 'custom_behavior');
      } else {
        // For original customers, replace all attributes
        if (onUpdateCustomer) {
          const updatedCustomer = {
            ...customer,
            custom_behavior: { 
              id: targetBehavior.id, 
              name: targetBehavior.name, 
              description: targetBehavior.description 
            },
            ap_archetype: undefined,
            governance_overlay: undefined
          };
          onUpdateCustomer(updatedCustomer);
        }
      }
    } else {
      // Handle other AP archetypes
      const targetArchetype = apArchetypes.find(arch => arch.name === targetName);
      if (!targetArchetype) return;
      
      // Remove the specific dragged customer_in_section
      if (setCustomersInSections) {
        setCustomersInSections(prev => 
          prev.filter(cis => cis.id !== draggedCustomer.id)
        );
      }
      
      // Add new customer_in_section
      const newCustomerInSection: CustomerInSection = {
        id: `customer_${draggedCustomer.customerId}_${targetArchetype.id}`,
        customerId: draggedCustomer.customerId,
        name: draggedCustomer.name,
        sectionType: 'ap_archetype',
        sectionId: targetArchetype.id,
        isDuplicated: draggedCustomer.isDuplicated
      };
      
      if (setCustomersInSections) {
        setCustomersInSections(prev => [...prev, newCustomerInSection]);
      }
      
      // Update the customer object
      if (draggedCustomer.isDuplicated) {
        // For duplicated customers, just add the new attribute
        updateCustomerAttributes(customer, { id: targetArchetype.id, name: targetArchetype.name, description: targetArchetype.description }, 'ap_archetype');
      } else {
        // For original customers, replace all attributes
        if (onUpdateCustomer) {
          const updatedCustomer = {
            ...customer,
            ap_archetype: { id: targetArchetype.id, name: targetArchetype.name, description: targetArchetype.description },
            governance_overlay: undefined,
            custom_behavior: undefined
          };
          onUpdateCustomer(updatedCustomer);
        }
      }
    }
    
    setDraggedCustomer(null);
  };

  const handleAddBehavior = () => {
    setShowBehaviorModal(true);
  };

  const handleSaveBehavior = () => {
    if (newBehaviorDescription.trim() && onAddCustomBehavior) {
      const newBehavior: CustomBehavior = {
        id: `custom_${Date.now()}`,
        name: `Custom Behavior ${customBehaviors.length + 1}`,
        description: newBehaviorDescription.trim()
      };
      
      onAddCustomBehavior(newBehavior);
      setNewBehaviorDescription('');
      setShowBehaviorModal(false);
    }
  };

  const handleCancelBehavior = () => {
    setNewBehaviorDescription('');
    setShowBehaviorModal(false);
  };

  const handleDuplicateCustomer = (customerInSection: CustomerInSection) => {
    // Create a new CustomerInSection object for the duplicated customer
    const duplicatedCustomerInSection: CustomerInSection = {
      id: `${customerInSection.id}_duplicate_${Date.now()}`,
      customerId: customerInSection.customerId,
      name: `${customerInSection.name} (D)`,
      sectionType: customerInSection.sectionType,
      sectionId: customerInSection.sectionId,
      isDuplicated: true
    };
    
    // Add the duplicated customer to the same section
    if (setCustomersInSections) {
      setCustomersInSections(prev => [...prev, duplicatedCustomerInSection]);
    }
  };

  const handleRemoveCustomer = (customerInSectionId: string) => {
    if (setCustomersInSections) {
      setCustomersInSections(prev => prev.filter(cis => cis.id !== customerInSectionId));
    }
  };

  // Function to update customer attributes when duplicated CustomerInSection moves
  const updateCustomerAttributes = (customer: Customer, newAttribute: any, attributeType: 'ap_archetype' | 'governance_overlay' | 'custom_behavior') => {
    if (!onUpdateCustomer) return;
    
    // For duplicated customers, we need to add the attribute to existing ones
    // For now, we'll just update the primary attribute as the current system expects
    const updatedCustomer = {
      ...customer,
      [attributeType]: newAttribute
    };
    
    onUpdateCustomer(updatedCustomer);
  };

  // Function to update customer with multiple attributes
  const updateCustomerWithMultipleAttributes = (customer: Customer, newAttribute: any, attributeType: 'ap_archetype' | 'governance_overlay' | 'custom_behavior') => {
    if (!onUpdateCustomer) return;

    // Find the original customer (without (D) suffix)
    const originalCustomerName = customer.name.replace(' (D)', '');
    const originalCustomer = customers.find(c => c.name === originalCustomerName && !c.name.includes(' (D)'));
    
    if (!originalCustomer) return;

    // Initialize multiple_attributes if it doesn't exist
    const multipleAttributes = originalCustomer.multiple_attributes || {
      ap_archetypes: [],
      governance_overlays: [],
      custom_behaviors: []
    };

    // Add the new attribute to the appropriate array
    if (attributeType === 'ap_archetype') {
      if (!multipleAttributes.ap_archetypes) multipleAttributes.ap_archetypes = [];
      // Check if this archetype is already in the list
      const existingIndex = multipleAttributes.ap_archetypes.findIndex(attr => attr.id === newAttribute.id);
      if (existingIndex === -1) {
        multipleAttributes.ap_archetypes.push(newAttribute);
      }
    } else if (attributeType === 'governance_overlay') {
      if (!multipleAttributes.governance_overlays) multipleAttributes.governance_overlays = [];
      // Check if this overlay is already in the list
      const existingIndex = multipleAttributes.governance_overlays.findIndex(attr => attr.id === newAttribute.id);
      if (existingIndex === -1) {
        multipleAttributes.governance_overlays.push(newAttribute);
      }
    } else if (attributeType === 'custom_behavior') {
      if (!multipleAttributes.custom_behaviors) multipleAttributes.custom_behaviors = [];
      // Check if this behavior is already in the list
      const existingIndex = multipleAttributes.custom_behaviors.findIndex(attr => attr.id === newAttribute.id);
      if (existingIndex === -1) {
        multipleAttributes.custom_behaviors.push(newAttribute);
      }
    }

    // Update the original customer with multiple attributes
    const updatedOriginalCustomer = {
      ...originalCustomer,
      multiple_attributes: multipleAttributes
    };

    onUpdateCustomer(updatedOriginalCustomer);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <div className="flex items-center mb-6">
        <div className={`flex items-center justify-center w-12 h-12 rounded-full text-2xl mr-4 ${
          status === 'active' ? 'bg-yellow-100 text-yellow-600' :
          'bg-gray-100 text-gray-400'
        }`}>
          ❓
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Step 2: Answer a few questions about your accounts</h3>
          <p className="text-gray-600">
            {status === 'active' ? 'To understand the context for AP archetypes and necessary governance for credit controlling and dunning.' :
             'Waiting for Xero authorization'}
          </p>
        </div>
      </div>

      {status === 'waiting' && (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <div className="text-4xl mb-4">⏳</div>
          <h4 className="text-lg font-semibold text-gray-700 mb-2">Waiting for Xero Connection</h4>
          <p className="text-gray-600">
            Complete the Xero authorization above to access contextualize questions.
          </p>
        </div>
      )}

      {status === 'active' && (
        <div className="space-y-6">
          {/* Loading state when data is not ready */}
          {(!customers || customers.length === 0 || !apArchetypes || apArchetypes.length === 0) && (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading customer data...</p>
            </div>
          )}

          {/* Content when data is ready */}
          {customers && customers.length > 0 && apArchetypes && apArchetypes.length > 0 && (
            <>
              {/* NO DUNNING Customers - Full Width */}
              {(() => {
                const { customers: paginatedCustomers, totalPages, currentPage: currentPageNum, totalCustomers, hasNextPage, hasPrevPage } = getFilteredAndPaginatedNoDunningCustomers();
                
                if (totalCustomers === 0) return null;
                
                return (
                  <div className="w-full">
                    <div 
                      className="bg-red-50 rounded-lg border-2 border-dashed border-red-300 p-4 min-h-[200px]"
                    >
                      <div className="mb-4">
                        <p className="text-md text-gray-600 mb-1">
                          <span className="font-semibold text-red-900">NO DUNNING:</span> These customers have been excluded from automated dunning processes due to their credit risk profile.
                        </p>
                        <div className="text-sm text-gray-500">
                          {totalCustomers} customer{totalCustomers !== 1 ? 's' : ''}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1 min-h-[120px]">
                        {paginatedCustomers.map((customerInSection) => (
                          <div
                            key={customerInSection.id}
                            className="bg-white border border-red-200 rounded py-6 px-2 hover:shadow-md transition-shadow text-center flex flex-col items-center justify-center h-16 w-32 relative group"
                          >
                            <div className="flex-1 flex items-center justify-center w-full">
                              <div className="flex items-center justify-center">
                                <span className="font-medium text-gray-900 text-xs leading-tight">
                                  {customerInSection.name}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {paginatedCustomers.length === 0 && (
                          <div className="col-span-full text-center text-gray-400 text-sm py-8">
                            No customers in this section
                          </div>
                        )}
                      </div>

                      {/* Pagination Controls for No Dunning */}
                      {totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            Showing {((currentPageNum - 1) * noDunningItemsPerPage) + 1} to {Math.min(currentPageNum * noDunningItemsPerPage, totalCustomers)} of {totalCustomers} customers
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleNoDunningPrevPage}
                              disabled={!hasPrevPage}
                              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            
                            <div className="flex space-x-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                  key={page}
                                  onClick={() => handleNoDunningPageChange(page)}
                                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                                    page === currentPageNum
                                      ? 'bg-red-600 text-white'
                                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              ))}
                            </div>
                            
                            <button
                              onClick={handleNoDunningNextPage}
                              disabled={!hasNextPage}
                              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* STANDARD_B2B Customers - Full Width */}
              {(() => {
                const standardB2BArchetype = apArchetypes.find(arch => arch.name === 'STANDARD_B2B');
                const { customers: paginatedCustomers, totalPages, currentPage: currentPageNum, totalCustomers, hasNextPage, hasPrevPage } = getFilteredAndPaginatedStandardB2BCustomers();
                const allStandardB2BCustomers = getCustomersByArchetype()['STANDARD_B2B'] || [];
                
                if (!standardB2BArchetype) return null;
                
                return (
                  <div className="w-full">
                    {/* Search Bar */}
                    <div className="mb-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search by customer name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div 
                      className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-4 min-h-[200px]"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, 'STANDARD_B2B')}
                    >
                      <div className="mb-4">
                        <p className="text-md text-gray-600 mb-1">
                          <span className="font-semibold text-gray-900">{standardB2BArchetype.name}:</span> {standardB2BArchetype.descriptions?.find(desc => desc.key === 'onboarding_question')?.value || standardB2BArchetype.description}
                        </p>
                        <div className="text-sm text-gray-500">
                          {searchTerm ? `${totalCustomers} of ${allStandardB2BCustomers.length} customers` : `${totalCustomers} customer${totalCustomers !== 1 ? 's' : ''}`}
                          {searchTerm && ` (filtered by "${searchTerm}")`}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1 min-h-[120px]">
                        {paginatedCustomers.map((customerInSection) => (
                          <div
                            key={customerInSection.id}
                            className="bg-white border border-gray-200 rounded py-6 px-2 hover:shadow-md transition-shadow text-center flex flex-col items-center justify-center h-16 w-32 relative group"
                          >
                            <div
                              draggable
                              onDragStart={(e) => handleDragStart(e, customerInSection)}
                              className="flex-1 flex items-center justify-center cursor-move w-full"
                            >
                              <div className="flex items-center justify-center">
                                <span className="font-medium text-gray-900 text-xs leading-tight">
                                  {customerInSection.name}
                                </span>
                              </div>
                            </div>
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateCustomer(customerInSection);
                                }}
                                className="bg-gray-100 hover:bg-gray-200 rounded-full p-1 text-gray-600 hover:text-gray-800"
                                title="Duplicate customer"
                                type="button"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                              {customerInSection.isDuplicated && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveCustomer(customerInSection.id);
                                  }}
                                  className="bg-red-100 hover:bg-red-200 rounded-full p-1 text-red-600 hover:text-red-800"
                                  title="Remove customer"
                                  type="button"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {paginatedCustomers.length === 0 && (
                          <div className="col-span-full text-center text-gray-400 text-sm py-8">
                            {searchTerm ? `No customers found matching "${searchTerm}"` : 'No customers in this archetype'}
                          </div>
                        )}
                      </div>

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            Showing {((currentPageNum - 1) * itemsPerPage) + 1} to {Math.min(currentPageNum * itemsPerPage, totalCustomers)} of {totalCustomers} customers
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handlePrevPage}
                              disabled={!hasPrevPage}
                              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            
                            <div className="flex space-x-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                                    page === currentPageNum
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              ))}
                            </div>
                            
                            <button
                              onClick={handleNextPage}
                              disabled={!hasNextPage}
                              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Other AP Archetypes - Two Column Grid */}
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {apArchetypes
                    .filter(archetype => archetype.name !== 'STANDARD_B2B')
                    .map((archetype) => {
                      const customersInArchetype = getCustomersByArchetype()[archetype.name] || [];
                      
                      return (
                        <div
                          key={archetype.id}
                          className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-4 min-h-[200px]"
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, archetype.name)}
                        >
                          <div className="mb-4">
                            {/* <h5 className="font-semibold text-gray-900 text-lg mb-2">
                              {archetype.name}
                              {archetype.is_default && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  Default
                                </span>
                              )}
                            </h5> */}
                            <p className="text-md text-gray-600 mb-1">
                              <span className="font-semibold text-gray-900">{archetype.name}:</span> {archetype.descriptions?.find(desc => desc.key === 'onboarding_question')?.value || archetype.description}
                            </p>
                            <div className="text-sm text-gray-500">
                              {customersInArchetype.length} customer{customersInArchetype.length !== 1 ? 's' : ''}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 min-h-[120px]">
                            {customersInArchetype.map((customerInSection) => (
                              <div
                                key={customerInSection.id}
                                className="bg-white border border-gray-200 rounded px-2 py-6 hover:shadow-md transition-shadow text-center flex flex-col items-center justify-center h-16 w-30 relative group"
                              >
                                <div
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, customerInSection)}
                                  className="flex-1 flex items-center justify-center cursor-move w-full"
                                >
                                  <div className="flex items-center justify-center">
                                    <span className="font-medium text-gray-900 text-xs leading-tight">
                                      {customerInSection.name}
                                    </span>
                                  </div>
                                </div>
                                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDuplicateCustomer(customerInSection);
                                    }}
                                    className="bg-gray-100 hover:bg-gray-200 rounded-full p-1 text-gray-600 hover:text-gray-800"
                                    title="Duplicate customer"
                                    type="button"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                  {customerInSection.isDuplicated && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveCustomer(customerInSection.id);
                                      }}
                                      className="bg-red-100 hover:bg-red-200 rounded-full p-1 text-red-600 hover:text-red-800"
                                      title="Remove customer"
                                      type="button"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                            
                            {customersInArchetype.length === 0 && (
                              <div className="col-span-full text-center text-gray-400 text-sm py-8">
                                Drop customers here
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Governance Overlays - Two Column Grid */}
              {governanceOverlays.length > 0 && (
                <div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {governanceOverlays.map((overlay) => {
                      // Count customers assigned to this governance overlay (only original customers for count)
                      const customersInOverlay = customersInSections.filter(customerInSection => 
                        customerInSection.sectionType === 'governance_overlay' && 
                        customerInSection.sectionId === overlay.id
                      ).length;
                      
                      return (
                        <div
                          key={overlay.id}
                          className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-4 min-h-[200px]"
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, `GOVERNANCE_OVERLAY_${overlay.id}`)}
                        >
                          <div className="mb-4">
                            <p className="text-md text-gray-600 mb-1">
                              <span className="font-semibold text-gray-900">{overlay.name}:</span> {overlay.descriptions?.find(desc => desc.key === 'onboarding_question')?.value || overlay.description}
                            </p>
                            <div className="text-sm text-gray-500">
                              {customersInOverlay} customer{customersInOverlay === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 min-h-[120px]">
                            {customersInSections
                              .filter(customerInSection => 
                                customerInSection.sectionType === 'governance_overlay' && 
                                customerInSection.sectionId === overlay.id
                              )
                              .map((customerInSection) => (
                                <div
                                  key={customerInSection.id}
                                  className="bg-white border border-gray-200 rounded px-2 py-6 hover:shadow-md transition-shadow text-center flex flex-col items-center justify-center h-16 w-30 relative group"
                                >
                                  <div
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, customerInSection)}
                                    className="flex-1 flex items-center justify-center cursor-move w-full"
                                  >
                                    <div className="flex items-center justify-center">
                                      <span className="font-medium text-gray-900 text-xs leading-tight">
                                        {customerInSection.name}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDuplicateCustomer(customerInSection);
                                      }}
                                      className="bg-gray-100 hover:bg-gray-200 rounded-full p-1 text-gray-600 hover:text-gray-800"
                                      title="Duplicate customer"
                                      type="button"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                    {customerInSection.isDuplicated && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveCustomer(customerInSection.id);
                                        }}
                                        className="bg-red-100 hover:bg-red-200 rounded-full p-1 text-red-600 hover:text-red-800"
                                        title="Remove customer"
                                        type="button"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            
                            {customersInOverlay === 0 && (
                              <div className="col-span-full text-center text-gray-400 text-sm py-8">
                                Drop customers here
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Behaviors - Two Column Grid */}
              {customBehaviors.length > 0 && (
                <div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {customBehaviors.map((behavior) => {
                      // Count customers assigned to this custom behavior (only original customers for count)
                      const customersInBehavior = customersInSections.filter(customerInSection => 
                        customerInSection.sectionType === 'custom_behavior' && 
                        customerInSection.sectionId === behavior.id
                      ).length;
                      
                      return (
                        <div
                          key={behavior.id}
                          className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-4 min-h-[200px]"
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, `CUSTOM_BEHAVIOR_${behavior.id}`)}
                        >
                          <div className="mb-4">
                            <p className="text-md text-gray-600 mb-1">
                              <span className="font-semibold text-gray-900">{behavior.name}:</span> {behavior.description}
                            </p>
                            <div className="text-sm text-gray-500">
                              {customersInBehavior} customer{customersInBehavior === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 min-h-[120px]">
                            {customersInSections
                              .filter(customerInSection => 
                                customerInSection.sectionType === 'custom_behavior' && 
                                customerInSection.sectionId === behavior.id
                              )
                              .map((customerInSection) => (
                                <div
                                  key={customerInSection.id}
                                  className="bg-white border border-gray-200 rounded px-2 py-6 hover:shadow-md transition-shadow text-center flex flex-col items-center justify-center h-16 w-30 relative group"
                                >
                                  <div
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, customerInSection)}
                                    className="flex-1 flex items-center justify-center cursor-move w-full"
                                  >
                                    <div className="flex items-center justify-center">
                                      <span className="font-medium text-gray-900 text-xs leading-tight">
                                        {customerInSection.name}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDuplicateCustomer(customerInSection);
                                      }}
                                      className="bg-gray-100 hover:bg-gray-200 rounded-full p-1 text-gray-600 hover:text-gray-800"
                                      title="Duplicate customer"
                                      type="button"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                    {customerInSection.isDuplicated && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveCustomer(customerInSection.id);
                                        }}
                                        className="bg-red-100 hover:bg-red-200 rounded-full p-1 text-red-600 hover:text-red-800"
                                        title="Remove customer"
                                        type="button"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            
                            {customersInBehavior === 0 && (
                              <div className="col-span-full text-center text-gray-400 text-sm py-8">
                                Drop customers here
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleAddBehavior}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
                >
                  <span className="mr-2">+</span>
                  Add Behavior
                </button>
                
                <button
                  onClick={onNextStep}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center"
                >
                  <span className="mr-2">✓</span>
                  Save & Continue
                </button>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h5 className="font-semibold text-green-900 mb-2">💡 Tips</h5>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Drag customers between archetypes to match their business characteristics</li>
                  <li>• Click the duplicate icon (📋) on any customer card to create a copy</li>
                  <li>• Duplicated customers can be moved to different sections to specify multiple behaviors</li>
                  <li>• The original customer will appear in all sections where it has attributes</li>
                  <li>• Multiple attributes are saved and will be shown in steps 4 and 5</li>
                  <li>• STANDARD_B2B is the default archetype for traditional B2B customers</li>
                  <li>• Use "Add Behavior" to create custom behavior categories for specific customer groups</li>
                  <li>• Duplicated customers are just a UI mechanism - only original customers are displayed</li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add Behavior Modal */}
      {showBehaviorModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="behavior-modal-title"
        >
          <FocusTrap>
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 id="behavior-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
                Add New Behavior
              </h3>
              
              <div className="mb-4">
                <label htmlFor="behavior-description" className="block text-sm font-medium text-gray-700 mb-2">
                  Behavior Description
                </label>
                <textarea
                  ref={textareaRef}
                  id="behavior-description"
                  value={newBehaviorDescription}
                  onChange={(e) => setNewBehaviorDescription(e.target.value)}
                  placeholder="Describe the behavior you want to track..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  aria-describedby="behavior-description-help"
                />
                <div id="behavior-description-help" className="text-xs text-gray-500 mt-1">
                  Describe the behavior you want to track for this customer
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelBehavior}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBehavior}
                  disabled={!newBehaviorDescription.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors"
                  type="button"
                >
                  Save
                </button>
              </div>
            </div>
          </FocusTrap>
        </div>
      )}
    </div>
  );
};

export default AskContextualizeQuestionsStep;
